import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@material-ui/core/styles';
import { viewPositionText } from '../../../../../helpers/qualityProjection';
import { useQualityTranslation } from '../../../../../helpers/hooks/useQualityTranslation';
import * as actions from '../../../../../store/index';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Fab,
  Grid,
  Tooltip,
  Typography,
} from '@material-ui/core';
import SummaryParagraphHeader from '../SummaryParagraphHeader';
import { getCurrentModuleStatus } from '../SummaryCommon/SummaryCommon';
import { ReviewerSelector } from '../SummaryCommon/ReviewerSelector';
import { QualityCustomChip } from './QualityChips';
import { SummaryCardsActions } from '../SummaryCommon/ModuleCardsActions';
import limitationsIcon from '../../../../../resources/icons/limitations_icon.svg';

const useStyles = makeStyles((theme) => ({
  quality_header_container: {
    display: 'flex',
    width: '100%',
    padding: '0px 8px',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '30px',
  },

  projection_grid_container: {
    display: 'flex',
    overflow: 'hidden',
    alignItems: 'center',
    margin: '4px',
  },

  projection_text: {
    fontSize: '0.6rem',
    minWidth: '35px',
    color: '#c2c2c2',
    padding: '4%',
    width: '45px',
  },

  tooltip_text: {
    letterSpacing: '0.09rem',
    fontSize: '12px',
  },

  card_main_container: {
    margin: '0',
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
  },

  // QualitySummary inline styles
  general_quality_chip_container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '85%',
    height: '30px',
    marginBottom: '4px',
  },

  card_header: {
    padding: '0',
    minHeight: '38px',
    height: '38px',
    backgroundColor: theme.palette.texts.generalTitleBackgrounds,
  },

  card_content: {
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    position: 'relative',
    cursor: 'pointer',
  },

  grid_container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },

  fab_icon_container: {
    margin: '2px',
    position: 'absolute',
    left: '4px',
    bottom: '4px',
  },

  fab_icon: {
    height: '20px',
    width: '20px',
    minWidth: '20px',
    minHeight: '20px',
    backgroundColor: theme.palette.accents.dark,
    '&:hover': {
      backgroundColor: theme.palette.accents.main,
      boxShadow: '0px 0px 5px 0px #d7c2c2',
    },
  },

  fab_icon_src: {
    height: '16px',
    width: '16px',
  },
}));

export default function QualitySummary(props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const classes = useStyles();

  const { landscape, zoomedProjection } = useSelector((state) => state.gui);
  const { premium_features, user_settings } = useSelector((state) => state.global_settings);
  const { inverted_view_position } = user_settings || {};

  const { diagnostics_microcalc_module, diagnostics_opacities_module, density_module, data_annotation } =
    premium_features;
  const { applied_quality, confirmed_quality, shown_quality, notes } = useSelector((state) => state.results);

  const [qualityPopperOpen, setQualityPopperOpen] = useState(false);
  const annotatorRole = data_annotation && localStorage.getItem('annotation') === 'true';
  const isConfirmed = applied_quality === 'bbox' && confirmed_quality;
  const isRelabeled = confirmed_quality && applied_quality !== 'bbox';
  const shownQuality = shown_quality || applied_quality;
  const currentRole = localStorage.getItem('role');
  const moduleStatus = getCurrentModuleStatus(
    applied_quality,
    shownQuality,
    isConfirmed,
    isRelabeled,
    t('ResultView.quality'),
    theme,
    t
  );

  const projectionsGridItems = inverted_view_position
    ? [
        {
          label: t('Dashboard.rmlo'),
          key: 'rmlo',
        },
        {
          label: t('Dashboard.lmlo'),
          key: 'lmlo',
        },
        {
          label: t('Dashboard.rcc'),
          key: 'rcc',
        },
        {
          label: t('Dashboard.lcc'),
          key: 'lcc',
        },
      ]
    : [
        {
          label: t('Dashboard.rcc'),
          key: 'rcc',
        },
        {
          label: t('Dashboard.lcc'),
          key: 'lcc',
        },
        {
          label: t('Dashboard.rmlo'),
          key: 'rmlo',
        },
        {
          label: t('Dashboard.lmlo'),
          key: 'lmlo',
        },
      ];

  // Effects
  useEffect(() => {
    setQualityPopperOpen(false);
  }, []);

  const generalQualityChip = () => {
    const projection =
      props?.results?.rcc || props?.results?.lcc || props?.results?.rmlo || props?.results?.lmlo;

    if (projection?.study_quality && !zoomedProjection) {
      return (
        <div className={classes.general_quality_chip_container}>
          <QualityCustomChip
            result={projection.study_quality}
            explanation={null}
            qualityPoints={null}
            projection='general'
            projLabel={t('ResultView.general_quality')}
          />
        </div>
      );
    }
    return null;
  };

  // Actions
  const handleEditButtonClick = () => {
    if (!!props?.results) {
      dispatch(actions.setTabsIndex(2));
      dispatch(actions.setQualityInRevisionMode(true));
    }
  };

  const handleTitleClick = () => {
    if (!!props?.results) {
      dispatch(actions.setTabsIndex(1));
    }
  };

  const onConfirmClick = () => {
    const state = shown_quality === applied_quality && confirmed_quality;
    dispatch(actions.confirmEvaluation(shownQuality, 'quality', !state, t));
  };

  return (
    <Card className={classes.card_main_container}>
      <CardHeader
        className={classes.card_header}
        title={
          <div className={classes.quality_header_container}>
            <SummaryParagraphHeader
              header={t('b_box.b_quality')}
              onClick={() => handleTitleClick()}
              tootlipTitle={t('ResultView.view_more_quality')}
            />
            <ReviewerSelector
              type='quality'
              shownContent={shownQuality}
              appliedContent={applied_quality}
              isConfirmed={isConfirmed}
              isRelabeled={isRelabeled}
              classes={classes}
              onPopperClose={() => setQualityPopperOpen(false)}
            />
          </div>
        }
      />
      <CardContent className={classes.card_content} onClick={() => handleTitleClick()}>
        {currentRole !== 'technician' && (
          <Tooltip
            placement='top-end'
            arrow
            title={
              <Typography className={classes.tooltip_text} color='inherit'>
                {t('report.limitations')}
              </Typography>
            }>
            <div className={classes.fab_icon_container}>
              <Badge overlap='circular' color='primary' variant='dot' invisible={!notes}>
                <Fab
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    dispatch(actions.setActiveDialog('limitations'));
                  }}
                  color='primary'
                  className={classes.fab_icon}>
                  <img src={limitationsIcon} alt='limitations' className={classes.fab_icon_src} />
                </Fab>
              </Badge>
            </div>
          </Tooltip>
        )}
        {generalQualityChip()}
        <Grid container className={classes.grid_container}>
          {zoomedProjection ? (
            <Grid item xs={10} className={classes.projection_grid_container}>
              {/* <Typography className={classes.projection_text}>{zoomedProjection.toUpperCase()}</Typography> */}
              <QualityCustomChip
                result={props?.results?.[zoomedProjection]?.quality}
                explanation={props?.results?.[zoomedProjection]?.quality_explanation}
                qualityPoints={props?.results?.[zoomedProjection]?.quality_points}
                projection={zoomedProjection}
                projLabel={zoomedProjection.toUpperCase()}
              />
            </Grid>
          ) : (
            projectionsGridItems.map((item) => (
              <Grid
                item
                xs={5}
                key={item.key}
                className={classes.projection_grid_container}
                style={{
                  justifyContent: item.key === 'rcc' || item.key === 'rmlo' ? 'flex-end' : 'flex-start',
                }}>
                {/* <Typography className={classes.projection_text}>{item.label}</Typography> */}
                <QualityCustomChip
                  result={props?.results?.[item.key]?.quality}
                  explanation={props?.results?.[item.key]?.quality_explanation}
                  qualityPoints={props?.results?.[item.key]?.quality_points}
                  projection={item.key}
                  projLabel={item.label}
                />
              </Grid>
            ))
          )}
        </Grid>
      </CardContent>
      <SummaryCardsActions
        isConfirmed={isConfirmed}
        isRelabeled={isRelabeled}
        annotatorRole={annotatorRole}
        moduleStatus={moduleStatus}
        handleEditButtonClick={handleEditButtonClick}
        onConfirmClick={onConfirmClick}
        module='quality'
        hasResults={!!props?.results}
      />
    </Card>
  );
}
